<script setup lang="ts">
/* =============================================================
   VESSEL — DETAIL
   =============================================================
   One ship of the fleet: what search matches it on, its bookings,
   and the way from here into a search already set up for it.
   The only place a vessel is edited or deleted from — you are
   looking at the whole record when you decide to destroy it.
   ============================================================= */

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import BookingsPanel from '@/components/BookingsPanel.vue'
import { routes, to } from '@/config/routes'
import { useAmenities } from '@/lib/amenities'
import { useAuth } from '@/lib/auth/useAuth'
import { useCountries } from '@/lib/countries'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { deleteVessel, getVessel } from '@/lib/vessels'
import { useVesselTypes } from '@/lib/vesselTypes'
import { errorMessage } from '@/lib/errors'
import type { Vessel } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { vesselTypeLabel } = useVesselTypes()
const { amenityLabel } = useAmenities()
const { countryName } = useCountries()

const vessel = ref<Vessel | null>(null)
const loading = ref(true)
const loadError = ref('')

const id = computed(() => Number(route.params.id))
const { user } = useAuth()
/* A berth owner can read the ships booked at its berths; only the operating
   company may edit, delete or book for one. */
const isOwn = computed(() => vessel.value?.company_id === user.value?.companyId)
useDocumentTitle(() => vessel.value?.name ?? 'Vessel')

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    vessel.value = await getVessel(id.value)
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load the vessel.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(id, load)

const rows = computed<DetailRow[]>(() => {
  const v = vessel.value
  if (!v) return []
  return [
    { label: 'Type', value: vesselTypeLabel(v.type) },
    { label: 'Length', value: `${v.length_m} m`, mono: true },
    { label: 'Beam', value: `${v.width_m} m`, mono: true },
    { label: 'Draft', value: `${v.draft_m} m`, mono: true },
    v.flag_code ? { label: 'Flag', value: countryName(v.flag_code) } : { label: 'Flag', empty: true },
    v.service_needs.length > 0
      ? { label: 'Service needs', tags: v.service_needs.map(amenityLabel) }
      : { label: 'Service needs', empty: true },
  ]
})

/* --- delete ------------------------------------------------------------- */

const confirming = ref(false)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onDeleteConfirmed(): Promise<void> {
  const v = vessel.value
  if (!v) return
  try {
    await deleteVessel(v.id)
    confirming.value = false
    await router.push(routes.vessels)
  } catch (e) {
    confirm.value?.fail(errorMessage(e, 'Could not delete the vessel.'))
  }
}
</script>

<template>
  <p v-if="loading && !vessel" class="page-status">Loading…</p>

  <template v-else-if="vessel">
    <PageHeader :title="vessel.name" :description="vesselTypeLabel(vessel.type)">
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.vessels" class="back-link">
          All vessels
        </Button>
        <template v-if="isOwn">
          <Button variant="secondary" icon="edit" :to="to.vesselEdit(vessel.id)">Edit</Button>
          <Button variant="danger" icon="trash" @click="confirming = true">Delete</Button>
        </template>
      </template>
    </PageHeader>

    <div class="sections">
      <Panel title="Details" description="Used to filter out berths the ship cannot use.">
        <template v-if="isOwn" #actions>
          <Button icon="search" size="sm" :to="to.search({ vessel: vessel.id })">Find a berth for this vessel</Button>
        </template>
        <DetailList :rows="rows" />
      </Panel>

      <BookingsPanel
        :filter="{ vesselId: vessel.id }"
        :hide="['vessel']"
        description="For this vessel, newest arrival first."
      />
    </div>

    <ConfirmDialog
      ref="confirm"
      :open="confirming"
      title="Delete vessel"
      :message="`“${vessel.name}” will be permanently removed. This cannot be undone.`"
      @close="confirming = false"
      @confirmed="onDeleteConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState
      v-else
      icon="ship"
      title="Vessel not found"
      description="It may have been deleted, or the link may be wrong."
    >
      <template #action>
        <Button :to="routes.vessels">All vessels</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}
</style>
